<template>
  <div class="bg-green-50 rounded-lg p-4 space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h4 class="text-sm font-medium text-green-900">📊 Progress Bar (Pixels 10-19)</h4>
        <p class="text-xs text-green-700">Visual countdown to special events</p>
      </div>
      <button
        @click="toggleProgressBar"
        :disabled="!isOnline"
        :class="[
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
          currentProgressBarEnabled 
            ? 'bg-green-600' 
            : 'bg-gray-200',
          !isOnline ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        ]"
      >
        <span
          :class="[
            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
            currentProgressBarEnabled ? 'translate-x-6' : 'translate-x-1'
          ]"
        />
      </button>
    </div>

    <div v-if="currentProgressBarEnabled" class="space-y-3">
      <!-- Start Date/Time -->
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs font-medium text-green-800 mb-1">Start Date</label>
          <input
            type="date"
            v-model="localProgressBarStartDate"
            @change="updateProgressBarConfig"
            :disabled="!isOnline"
            class="w-full px-2 py-1 text-xs border border-green-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
          >
        </div>
        <div>
          <label class="block text-xs font-medium text-green-800 mb-1">Start Time</label>
          <input
            type="time"
            v-model="localProgressBarStartTime"
            @change="updateProgressBarConfig"
            :disabled="!isOnline"
            class="w-full px-2 py-1 text-xs border border-green-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
          >
        </div>
      </div>

      <!-- End Date/Time -->
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs font-medium text-green-800 mb-1">End Date</label>
          <input
            type="date"
            v-model="localProgressBarEndDate"
            @change="updateProgressBarConfig"
            :disabled="!isOnline"
            class="w-full px-2 py-1 text-xs border border-green-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
          >
        </div>
        <div>
          <label class="block text-xs font-medium text-green-800 mb-1">End Time</label>
          <input
            type="time"
            v-model="localProgressBarEndTime"
            @change="updateProgressBarConfig"
            :disabled="!isOnline"
            class="w-full px-2 py-1 text-xs border border-green-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
          >
        </div>
      </div>

      <!-- Quick Set Buttons -->
      <div class="flex space-x-2">
        <button
          @click="setProgressBarToNow('start')"
          :disabled="!isOnline"
          class="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          Set Start to Now
        </button>
        <button
          @click="setProgressBarToNow('end')"
          :disabled="!isOnline"
          class="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          Set End to Now
        </button>
      </div>

      <!-- Message -->
      <div>
        <label class="block text-xs font-medium text-green-800 mb-1">Custom Message</label>
        <input
          type="text"
          v-model="localProgressBarMessage"
          @input="debounceProgressBarMessage"
          :disabled="!isOnline"
          maxlength="18"
          placeholder="e.g., 'Days until anniversary'"
          class="w-full px-2 py-1 text-xs border border-green-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
        >
      </div>

      <!-- Finish Message -->
      <div>
        <div class="flex items-center mb-2">
          <label class="block text-xs font-medium text-green-800 mr-2">Finish Message</label>
          <button
            @click="toggleProgressBarFinishMessage"
            :disabled="!isOnline"
            class="relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            :class="currentProgressBarFinishMessageEnabled ? 'bg-green-600' : 'bg-gray-300'"
          >
            <span
              class="inline-block h-3 w-3 transform rounded-full bg-white transition"
              :class="currentProgressBarFinishMessageEnabled ? 'translate-x-4' : 'translate-x-0.5'"
            ></span>
          </button>
        </div>
        <input
          v-if="currentProgressBarFinishMessageEnabled"
          type="text"
          v-model="localProgressBarFinishMessage"
          @input="debounceProgressBarFinishMessage"
          :disabled="!isOnline"
          maxlength="18"
          placeholder="e.g., 'Anniversary reached!'"
          class="w-full px-2 py-1 text-xs border border-green-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
        >
      </div>

      <!-- Finish Effects -->
      <div>
        <label class="block text-xs font-medium text-green-800 mb-1">Finish Effect</label>
        <select
          v-model="localProgressBarFinishEffect"
          @change="debounceProgressBarFinishEffect"
          :disabled="!isOnline"
          class="w-full px-2 py-1 text-xs border border-green-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
        >
          <option value="none">None</option>
          <option value="flash off">Flash Off</option>
          <option value="flash on">Flash On</option>
          <option value="running">Running</option>
        </select>
      </div>

      <!-- Finish Effect Color (only for flash on and running) -->
      <div v-if="localProgressBarFinishEffect === 'flash on' || localProgressBarFinishEffect === 'running'">
        <label class="block text-xs font-medium text-green-800 mb-1">Effect Color</label>
        <div class="grid grid-cols-3 gap-1">
          <input type="range" min="0" max="255" v-model.number="localProgressBarFinishEffectColor.r" @input="debounceProgressBarFinishEffect" class="h-1">
          <input type="range" min="0" max="255" v-model.number="localProgressBarFinishEffectColor.g" @input="debounceProgressBarFinishEffect" class="h-1">
          <input type="range" min="0" max="255" v-model.number="localProgressBarFinishEffectColor.b" @input="debounceProgressBarFinishEffect" class="h-1">
        </div>
        <div class="text-xs text-green-700">RGB: {{ localProgressBarFinishEffectColor.r }}, {{ localProgressBarFinishEffectColor.g }}, {{ localProgressBarFinishEffectColor.b }}</div>
      </div>

      <!-- Effect Speed (only for non-none effects) -->
      <div v-if="localProgressBarFinishEffect !== 'none'">
        <label class="block text-xs font-medium text-green-800 mb-1">Effect Speed: {{ localProgressBarFinishEffectSpeed.toFixed(1) }}x</label>
        <input
          type="range"
          min="0.1"
          max="3.0"
          step="0.1"
          v-model.number="localProgressBarFinishEffectSpeed"
          @input="debounceProgressBarFinishEffect"
          :disabled="!isOnline"
          class="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer"
        >
      </div>

      <!-- Colors -->
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs font-medium text-green-800 mb-1">Progress Color</label>
          <div class="grid grid-cols-3 gap-1">
            <input type="range" min="0" max="255" v-model.number="localProgressBarColor.r" @input="debounceProgressBarColors" class="h-1">
            <input type="range" min="0" max="255" v-model.number="localProgressBarColor.g" @input="debounceProgressBarColors" class="h-1">
            <input type="range" min="0" max="255" v-model.number="localProgressBarColor.b" @input="debounceProgressBarColors" class="h-1">
          </div>
          <div class="text-xs text-green-700">RGB: {{ localProgressBarColor.r }}, {{ localProgressBarColor.g }}, {{ localProgressBarColor.b }}</div>
        </div>
        <div>
          <label class="block text-xs font-medium text-green-800 mb-1">Background Color</label>
          <div class="grid grid-cols-3 gap-1">
            <input type="range" min="0" max="255" v-model.number="localProgressBarBgColor.r" @input="debounceProgressBarColors" class="h-1">
            <input type="range" min="0" max="255" v-model.number="localProgressBarBgColor.g" @input="debounceProgressBarColors" class="h-1">
            <input type="range" min="0" max="255" v-model.number="localProgressBarBgColor.b" @input="debounceProgressBarColors" class="h-1">
          </div>
          <div class="text-xs text-green-700">RGB: {{ localProgressBarBgColor.r }}, {{ localProgressBarBgColor.g }}, {{ localProgressBarBgColor.b }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  device: {
    type: Object,
    required: true
  },
  isOnline: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['update-desired'])

// Local state
const localProgressBarStartDate = ref('')
const localProgressBarStartTime = ref('00:00')
const localProgressBarEndDate = ref('')
const localProgressBarEndTime = ref('23:59')
const localProgressBarMessage = ref('')
const localProgressBarFinishMessage = ref('')
const localProgressBarFinishEffect = ref('none')
const localProgressBarFinishEffectColor = ref({ r: 255, g: 255, b: 255 })
const localProgressBarFinishEffectSpeed = ref(1.0)
const localProgressBarColor = ref({ r: 0, g: 255, b: 0 })
const localProgressBarBgColor = ref({ r: 20, g: 20, b: 20 })
let ledUpdateTimer = null

// Computed properties
const currentLedConfig = computed(() =>
  props.device.shadow?.desired?.ledConfig || {}
)

const currentProgressBarEnabled = computed({
  get: () => currentLedConfig.value?.progressBar?.enabled || false,
  set: (value) => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.progressBar.enabled = value
    updateDesiredState({ ledConfig: completeConfig })
  }
})

const currentProgressBarFinishMessageEnabled = computed({
  get: () => currentLedConfig.value?.progressBar?.finishMessageEnabled || false,
  set: (value) => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.progressBar.finishMessageEnabled = value
    updateDesiredState({ ledConfig: completeConfig })
  }
})

// Helper function to create complete LED config objects
const createCompleteLedConfig = () => {
  const current = currentLedConfig.value || {}
  return {
    moodLight: {
      enabled: current.moodLight?.enabled || false,
      mode: current.moodLight?.mode || (current.moodLight?.rainbow ? 'rainbow' : 'static'),
      color: current.moodLight?.color || { r: 255, g: 100, b: 100 }
    },
    progressBar: {
      enabled: current.progressBar?.enabled || false,
      startDateTime: current.progressBar?.startDateTime || `${localProgressBarStartDate.value}T${localProgressBarStartTime.value}:00`,
      endDateTime: current.progressBar?.endDateTime || `${localProgressBarEndDate.value}T${localProgressBarEndTime.value}:00`,
      message: current.progressBar?.message || localProgressBarMessage.value,
      finishMessageEnabled: current.progressBar?.finishMessageEnabled || false,
      finishMessage: current.progressBar?.finishMessage || localProgressBarFinishMessage.value,
      color: current.progressBar?.color || localProgressBarColor.value,
      backgroundColor: current.progressBar?.backgroundColor || localProgressBarBgColor.value,
      effect: current.progressBar?.effect || 'solid',
      finishEffect: current.progressBar?.finishEffect || localProgressBarFinishEffect.value,
      finishEffectColor: current.progressBar?.finishEffectColor || localProgressBarFinishEffectColor.value,
      finishEffectSpeed: current.progressBar?.finishEffectSpeed !== undefined ? current.progressBar.finishEffectSpeed : localProgressBarFinishEffectSpeed.value
    }
  }
}

// Watch for LED configuration changes
watch(() => currentLedConfig.value?.progressBar?.startDateTime, (newStartDateTime) => {
  if (newStartDateTime) {
    const [date, time] = newStartDateTime.split('T')
    localProgressBarStartDate.value = date
    localProgressBarStartTime.value = time?.slice(0, 5) || '00:00'
  }
}, { immediate: true })

watch(() => currentLedConfig.value?.progressBar?.endDateTime, (newEndDateTime) => {
  if (newEndDateTime) {
    const [date, time] = newEndDateTime.split('T')
    localProgressBarEndDate.value = date
    localProgressBarEndTime.value = time?.slice(0, 5) || '23:59'
  }
}, { immediate: true })

watch(() => currentLedConfig.value?.progressBar?.message, (newMessage) => {
  if (newMessage !== undefined) {
    localProgressBarMessage.value = newMessage
  }
}, { immediate: true })

watch(() => currentLedConfig.value?.progressBar?.finishMessage, (newFinishMessage) => {
  if (newFinishMessage !== undefined) {
    localProgressBarFinishMessage.value = newFinishMessage
  }
}, { immediate: true })

watch(() => currentLedConfig.value?.progressBar?.color, (newColor) => {
  if (newColor) {
    localProgressBarColor.value = { ...newColor }
  }
}, { immediate: true, deep: true })

watch(() => currentLedConfig.value?.progressBar?.backgroundColor, (newBgColor) => {
  if (newBgColor) {
    localProgressBarBgColor.value = { ...newBgColor }
  }
}, { immediate: true, deep: true })

watch(() => currentLedConfig.value?.progressBar?.finishEffect, (newFinishEffect) => {
  if (newFinishEffect !== undefined) {
    localProgressBarFinishEffect.value = newFinishEffect
  }
}, { immediate: true })

watch(() => currentLedConfig.value?.progressBar?.finishEffectColor, (newFinishEffectColor) => {
  if (newFinishEffectColor) {
    localProgressBarFinishEffectColor.value = { ...newFinishEffectColor }
  }
}, { immediate: true, deep: true })

watch(() => currentLedConfig.value?.progressBar?.finishEffectSpeed, (newFinishEffectSpeed) => {
  if (newFinishEffectSpeed !== undefined) {
    localProgressBarFinishEffectSpeed.value = newFinishEffectSpeed
  }
}, { immediate: true })

// Methods
const updateDesiredState = (newState) => {
  emit('update-desired', props.device.deviceId, newState)
}

const toggleProgressBar = () => {
  if (!props.isOnline) return
  currentProgressBarEnabled.value = !currentProgressBarEnabled.value
}

const toggleProgressBarFinishMessage = () => {
  if (!props.isOnline) return
  currentProgressBarFinishMessageEnabled.value = !currentProgressBarFinishMessageEnabled.value
}

const debounceProgressBarFinishMessage = () => {
  clearTimeout(ledUpdateTimer)
  ledUpdateTimer = setTimeout(() => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.progressBar.finishMessage = localProgressBarFinishMessage.value
    updateDesiredState({ ledConfig: completeConfig })
  }, 500)
}

const debounceProgressBarFinishEffect = () => {
  clearTimeout(ledUpdateTimer)
  ledUpdateTimer = setTimeout(() => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.progressBar.finishEffect = localProgressBarFinishEffect.value
    completeConfig.progressBar.finishEffectColor = { ...localProgressBarFinishEffectColor.value }
    completeConfig.progressBar.finishEffectSpeed = localProgressBarFinishEffectSpeed.value
    updateDesiredState({ ledConfig: completeConfig })
  }, 500)
}

const debounceProgressBarColors = () => {
  updateProgressBarColor()
}

const updateProgressBarConfig = () => {
  updateProgressBarDateTime()
}

const debounceProgressBarMessage = () => {
  clearTimeout(ledUpdateTimer)
  ledUpdateTimer = setTimeout(() => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.progressBar.message = localProgressBarMessage.value
    updateDesiredState({ ledConfig: completeConfig })
  }, 500)
}

const setProgressBarToNow = (type) => {
  const now = new Date()
  const date = now.toISOString().split('T')[0]
  const time = now.toTimeString().slice(0, 5)
  
  if (type === 'start') {
    localProgressBarStartDate.value = date
    localProgressBarStartTime.value = time
  } else if (type === 'end') {
    localProgressBarEndDate.value = date
    localProgressBarEndTime.value = time
  }
  updateProgressBarDateTime()
}

const updateProgressBarDateTime = () => {
  if (!props.isOnline) return
  clearTimeout(ledUpdateTimer)
  ledUpdateTimer = setTimeout(() => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.progressBar.startDateTime = `${localProgressBarStartDate.value}T${localProgressBarStartTime.value}:00`
    completeConfig.progressBar.endDateTime = `${localProgressBarEndDate.value}T${localProgressBarEndTime.value}:00`
    completeConfig.progressBar.message = localProgressBarMessage.value
    updateDesiredState({ ledConfig: completeConfig })
  }, 500)
}

const updateProgressBarColor = () => {
  if (!props.isOnline) return
  clearTimeout(ledUpdateTimer)
  ledUpdateTimer = setTimeout(() => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.progressBar.color = { ...localProgressBarColor.value }
    completeConfig.progressBar.backgroundColor = { ...localProgressBarBgColor.value }
    updateDesiredState({ ledConfig: completeConfig })
  }, 300)
}
</script>